import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Divider, Form, Icon, Modal } from "semantic-ui-react";

import { CUSTOM_CONFIRM_TYPES, Can, CustomConfirm, usePermanentRealtimeDealers } from "components";
import { createFormHelper } from "lib/Form";
import { useDeleteHoldingMutation } from "modules/HoldingsDashboard/components/CreateUpdateHoldingModal/hooks";
import { useHoldingsQuery } from "modules/HoldingsDashboard/hooks";

import type { Holding } from "models";
import type { CreateHoldingPayload } from "modules/HoldingsDashboard/components/CreateUpdateHoldingModal/hooks";

type CreateUpdateHoldingFormProps = {
  holding: Holding | null;
  hasErrors: boolean;
  isPending: boolean;
  onClose: () => void;
};

const FormHelper = createFormHelper<CreateHoldingPayload>();

export const CreateUpdateHoldingForm = ({ holding, hasErrors, isPending, onClose }: CreateUpdateHoldingFormProps) => {
  const { t } = useTranslation();

  const { watch } = FormHelper.useFormContext();

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const { dealers } = usePermanentRealtimeDealers();
  const { data: holdings } = useHoldingsQuery();
  const { mutateAsync: deleteHolding, isPending: isDeletePending } = useDeleteHoldingMutation();

  const [name, dealerIds] = watch(["name", "dealer_ids"]);

  const hasDealersAssignedToOtherHoldings = (dealerId: number) => holdings?.some(h => h.id !== holding?.id && h.dealer_ids.includes(dealerId)) ?? false;

  const dealerOptions = dealers.filter(dealer => !hasDealersAssignedToOtherHoldings(dealer.id)).map(({ id, name }) => ({ key: id, value: id, text: name }));

  const handleDeleteHolding = async () => {
    if (!holding) return;
    await deleteHolding(holding.id);
    onClose();
  };

  return (
    <>
      <Modal.Header className="padding-container">
        <div className="header-container">
          <div className="header-title">{!holding ? t("v8_add_holding", "Add Holding") : t("v8_edit_holding", "Edit Holding")}</div>
        </div>
      </Modal.Header>
      <Divider fitted />

      <Modal.Content className="padding-container">
        <Form.Field required error={!name && hasErrors}>
          <label>{t("v8_name", "Name")} </label>
          <FormHelper.Field.Input name="name" inputProps={{ placeholder: t("v8_name", "Name"), fluid: true }} />
        </Form.Field>

        <Form.Field required error={!dealerIds.length && hasErrors}>
          <label>{t("v8_dealers", "Dealers")} </label>
          <FormHelper.Field.Dropdown
            name="dealer_ids"
            options={dealerOptions}
            dropdownProps={{
              placeholder: t("v8_select_dealers", "Select Dealers"),
              clearable: true,
              search: true,
              selectOnBlur: false
            }}
          />
        </Form.Field>
      </Modal.Content>
      <Divider fitted />

      <Modal.Actions className="padding-container">
        <div className="actions-buttons-main-container">
          {holding && (
            <Can I="delete" the="holding">
              <div className="action-buttons-container">
                <Form.Button color="red" type="button" onClick={() => setShowDeleteConfirm(true)}>
                  {t("v8_delete", "Delete")}
                  <Icon className="trash" />
                </Form.Button>
              </div>
            </Can>
          )}

          <div className="save-cancel-buttons-container">
            <Form.Button className="light" type="button" onClick={onClose}>
              {t("v8_cancel", "Cancel")}
              <Icon className="xmark" />
            </Form.Button>
            <Can I={holding ? "update" : "create"} the="holding">
              <Form.Button type="submit" color="green" disabled={isPending} loading={isPending}>
                {!holding ? t("v8_save", "Save") : t("v8_update", "Update")}
                <Icon className="check" />
              </Form.Button>
            </Can>
          </div>
        </div>
      </Modal.Actions>

      <CustomConfirm
        type={CUSTOM_CONFIRM_TYPES.Danger}
        isOpen={showDeleteConfirm}
        handleCancel={() => setShowDeleteConfirm(false)}
        confirmMsg={t("v8_delete_confirm_message", "Are you sure that you want to delete this? You can't undo this action.")}
        handleConfirm={handleDeleteHolding}
        isLoading={isDeletePending}
      />
    </>
  );
};
